import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useRecoilState, useRecoilValue } from "recoil";
import CancelButton from "../../../../components/CancelButton";
import CheckBoxLabelHookForm from "../../../../components/CheckBoxLabelHookForm";
import NumericInputLabel from "../../../../components/NumericInputLabel";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import { toast } from "../../../../components/toastNotification";
import { accountAtom } from "../../../Login/atoms/AccountAtom";
import {
  agmtEditMainState,
  agmtGrpFilterParams,
  agmtSelectedRows,
  agmtToExpire,
} from "../atoms/atoms";
import AttachmentModalMainEdit from "./AttachmentlMainEdit";
import { useGetCarrierNameList } from "modules/CarrierAgreementMgmt/Carrier/hooks/useGetCarrierNameList";

const AgrmtMasterEditForm = ({
  agmtGroupDetails,
  isSaving,
  cancelEdit,
  setOpenConfirmSave,
  mainNewAttachments,
  forDeleteAttachments,
  mainOldAttachments,
  setMainOldAttachments,
  setMainNewAttachments,
  setForDeleteAttachments,
  setUpdatedMainRow,
}) => {
  const user = useRecoilValue(accountAtom);
  const editMainState = useRecoilValue(agmtEditMainState);
  const selectedAgmts = useRecoilValue(agmtSelectedRows);
  const [filterGrpParams, setFilterGrpParams] =
    useRecoilState<any>(agmtGrpFilterParams);
  const [toExpire, setToExpire] = useRecoilState(agmtToExpire);

  //Get Carriers for dropdown options
  const { data: carriers, isLoading: carriersLoading } =
    useGetCarrierNameList();
  const carrierOptions: any = Array.from(
    new Map(
      carriers
        ?.filter((x) => x.isActive === true)
        ?.map((x) => [
          x.masterCarrierId,
          {
            label: x.carrierName,
            value: x.masterCarrierId,
          },
        ])
    ).values()
  );

  const {
    formState: { errors, isDirty },
    control,
    register,
    handleSubmit,
    setValue,
    watch,
  } = useForm({ defaultValues: agmtGroupDetails[0] });

  const validateChanges = (data) => {
    const carrierName = carrierOptions?.filter(
      (x) => x.value === data.masterCarrierId
    )[0]?.label;
    if (data.masterCarrierId) {
      if (isDirty || mainNewAttachments || forDeleteAttachments) {
        if (selectedAgmts.length > 0) {
          if (
            data.masterCarrierId !== agmtGroupDetails[0].masterCarrierId ||
            data.effectiveDate !== agmtGroupDetails[0].effectiveDate ||
            data.expirationDate !== agmtGroupDetails[0].expirationDate ||
            data.newPercentage !== agmtGroupDetails[0].newPercentage ||
            data.newPercentageConfie !==
              agmtGroupDetails[0].newPercentageConfie ||
            data.newPercentageFranchise !==
              agmtGroupDetails[0].newPercentageFranchise ||
            data.renewalPercentage !== agmtGroupDetails[0].renewalPercentage ||
            data.renewPercentageConfie !==
              agmtGroupDetails[0].renewPercentageConfie ||
            data.renewPercentageFranchise !==
              agmtGroupDetails[0].renewPercentageFranchise ||
            data.flatRatePerPolicy !== agmtGroupDetails[0].flatRatePerPolicy ||
            data.newBusinessFlatRate !==
              agmtGroupDetails[0].newBusinessFlatRate ||
            data.renewalFlatRate !== agmtGroupDetails[0].renewalFlatRate
          ) {
            if (filterGrpParams.AgreementRepositoryId.length > 1) {
              setFilterGrpParams({
                ...filterGrpParams,
                AgreementRepositoryId: selectedAgmts,
              });
            }
          }
          setUpdatedMainRow({
            agreementRepositoryId: selectedAgmts,
            carrier: carrierName,
            masterCarrierId: data.masterCarrierId,
            newPercentage: data.newPercentage,
            newPercentageConfie: data.newPercentageConfie,
            newPercentageFranchise: data.newPercentageFranchise,
            renewalPercentage: data.renewalPercentage,
            renewPercentageConfie: data.renewPercentageConfie,
            renewPercentageFranchise: data.renewPercentageFranchise,
            effectiveDate: data.effectiveDate ? data.effectiveDate : null,
            expirationDate: data.expirationDate ? data.expirationDate : null,
            attachments: mainNewAttachments,
            attachmentsForDelete: forDeleteAttachments,
            flatRatePerPolicy: data.flatRatePerPolicy,
            newBusinessFlatRate: data.newBusinessFlatRate,
            renewalFlatRate: data.renewalFlatRate,
          });
          setOpenConfirmSave(true);
        } else {
          toast.fire(
            "No selected row",
            "Please select active Agreement row/s using the checkbox column before saving changes",
            "warning"
          );
        }
      } else {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    } else {
      toast.fire("Edit Agreement", "Missing Required Fields", "warning");
    }
  };

  return (
    <div className="px-2">
      <form onSubmit={handleSubmit(validateChanges)}>
        <div className="flex justify-between py-2 px-2 border-b-2 border-secondary">
          <header className="text-base text-primary font-bold">
            Edit Master Agreement
          </header>
          <div className="flex flex-row space-x-1">
            <SubmitHookForm
              type="submit"
              disabled={isSaving}
              buttonText={isSaving ? "Saving.." : "Save"}
              className={undefined}
              onClick={undefined}
            />
            <CancelButton
              onClick={() => {
                cancelEdit();
              }}
              disabled={isSaving}
            />
          </div>
        </div>
        <div className="text-xs text-secondary italic font-normal px-2 mt-2">
          <b> Note:</b> All fields with * are required to fill.
        </div>
        <div className="flex flex-row items-baseline">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 py-4 px-2">
            <div className="flex flex-row items-center space-x-1">
              <Controller
                control={control}
                name="masterCarrierId"
                rules={{
                  required: !toExpire,
                  validate: (value) =>
                    carrierOptions &&
                    carrierOptions?.findIndex((c) => c.value === value) >= 0,
                }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={carrierOptions}
                    isLoading={carriersLoading}
                    label="Carrier"
                    className={
                      errors.masterCarrierId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={!toExpire}
                    placeHolder={agmtGroupDetails[0].carrier}
                  />
                )}
              />
            </div>
            <TextInputLabelHookForm
              name="effectiveDate"
              label="Effective Date"
              type="date"
              register={register("effectiveDate", {})}
              disabled={true}
            />
            <TextInputLabelHookForm
              name="expirationDate"
              label="Expiration Date"
              type="date"
              min={watch("effectiveDate")}
              register={register("expirationDate", {})}
              onChange={(e) => {
                if (e.target.value) {
                  setToExpire(true);
                } else {
                  setToExpire(false);
                }
              }}
            />
            <CheckBoxLabelHookForm
              name="flatRatePerPolicy"
              label="Flat Rate Per Policy"
              register={register}
              onClick={() => {
                setValue("newBusinessFlatRate", null);
                setValue("renewalFlatRate", null);
                setValue("newPercentage", 0);
                setValue("renewalPercentage", 0);
                setValue("newPercentageConfie", 0);
                setValue("newPercentageFranchise", 0);
                setValue("renewPercentageConfie", 0);
                setValue("renewPercentageFranchise", 0);
              }}
            />

            {watch("flatRatePerPolicy") ? (
              <>
                <NumericInputLabel
                  label="New Flat Rate"
                  pattern="/^\[0-9]{6}\.?\[0-9{2}$/i,"
                  type="number"
                  step="0.01"
                  min="0"
                  max="99999.99"
                  register={register("newBusinessFlatRate", {
                    required: !toExpire && watch("flatRatePerPolicy"),
                  })}
                  disabled={!watch("flatRatePerPolicy")}
                  required={!toExpire && watch("flatRatePerPolicy")}
                  className={
                    errors.newBusinessFlatRate &&
                    "border-2 border-red-500 col-span-2"
                  }
                />
                <NumericInputLabel
                  label="Renewal Flat Rate"
                  pattern="/^\[0-9]{6}\.?\[0-9{2}$/i,"
                  type="number"
                  step="0.01"
                  min="0"
                  max="99999.99"
                  register={register("renewalFlatRate", {
                    required: !toExpire && watch("flatRatePerPolicy"),
                  })}
                  disabled={!watch("flatRatePerPolicy")}
                  required={!toExpire && watch("flatRatePerPolicy")}
                  className={
                    errors.renewalFlatRate &&
                    "border-2 border-red-500 col-span-2"
                  }
                />
              </>
            ) : (
              <>
                <NumericInputLabel
                  name="newPercentage"
                  label="New %"
                  register={register("newPercentage", {
                    required:
                      !toExpire && !agmtGroupDetails[0]?.flatRatePerPolicy,
                  })}
                  step="0.01"
                  min="0"
                  max="100.00"
                  required={
                    !toExpire && !agmtGroupDetails[0]?.flatRatePerPolicy
                  }
                  className={
                    errors.newPercentage && "border-2 border-red-500 col-span-2"
                  }
                  pattern="\b(0*(?:[1-9][0-9]?|100))\b"
                  onBlur={(e) => {
                    if (e.target.value) {
                      setValue(
                        "newPercentage",
                        parseFloat(e.target.value).toFixed(2),
                        {
                          shouldDirty:
                            parseFloat(e.target.value).toFixed(2) !==
                            agmtGroupDetails[0]?.newPercentage
                              ? true
                              : false,
                        }
                      );
                    }
                  }}
                  onChange={(e) => {
                    setValue("newPercentage", e ? e.target.value : null);
                    setValue("newPercentageConfie", "");
                    setValue("newPercentageFranchise", "");
                  }}
                />
                <NumericInputLabel
                  name="renewalPercentage"
                  label="Renewal %"
                  register={register("renewalPercentage", {
                    required:
                      !toExpire && !agmtGroupDetails[0]?.flatRatePerPolicy,
                  })}
                  step="0.01"
                  min="0"
                  max="100.00"
                  required={
                    !toExpire && !agmtGroupDetails[0]?.flatRatePerPolicy
                  }
                  type="number"
                  className={
                    errors.renewalPercentage &&
                    "border-2 border-red-500 col-span-2 "
                  }
                  pattern="\b(0*(?:[1-9][0-9]?|100))\b"
                  onBlur={(e) => {
                    if (e.target.value) {
                      setValue(
                        "renewalPercentage",
                        parseFloat(e.target.value).toFixed(2),
                        {
                          shouldDirty:
                            parseFloat(e.target.value).toFixed(2) !==
                            agmtGroupDetails[0]?.renewalPercentage
                              ? true
                              : false,
                        }
                      );
                    }
                  }}
                  onChange={(e) => {
                    setValue("renewalPercentage", e ? e.target.value : null);
                    setValue("renewPercentageConfie", "");
                    setValue("renewPercentageFranchise", "");
                  }}
                />
                {user.isFranchise && (
                  <>
                    <div className="sm:col-start-2 grid grid-cols-1 md:grid-cols-2 justify-items-end gap-1  ">
                      <div className="md:col-start-1 w-full">
                        <NumericInputLabel
                          name="newPercentageConfie"
                          label="Confie"
                          register={register("newPercentageConfie", {
                            required:
                              !toExpire &&
                              user.isFranchise &&
                              !user.isNonFranchise &&
                              !agmtGroupDetails[0]?.flatRatePerPolicy,
                          })}
                          disabled={
                            watch("newPercentage") === "" ||
                            watch("newPercentage") === "0"
                          }
                          step="0.01"
                          min="0"
                          max="100.00"
                          width="w-10"
                          hidden={
                            agmtGroupDetails[0]?.flatRatePerPolicy ||
                            !user.isFranchise
                          }
                          required={
                            !toExpire &&
                            user.isFranchise &&
                            !user.isNonFranchise &&
                            !agmtGroupDetails[0]?.flatRatePerPolicy
                          }
                          type="number"
                          className={
                            errors.newPercentageConfie &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          pattern="\b(0*(?:[1-9][0-9]?|100))\b"
                          onBlur={(e: any) => {
                            if (e.target.value) {
                              setValue(
                                "newPercentageConfie",
                                parseFloat(e.target.value).toFixed(2),
                                {
                                  shouldDirty:
                                    parseFloat(e.target.value).toFixed(2) !==
                                    agmtGroupDetails[0]?.newPercentageConfie
                                      ? true
                                      : false,
                                }
                              );
                              const newFran = (
                                parseInt(watch("newPercentage")) -
                                e.target.value
                              ).toString();
                              setValue(
                                "newPercentageFranchise",
                                parseFloat(newFran).toFixed(2)
                              );
                            }
                          }}
                          secondDiv="grid grid-cols-1"
                        />
                      </div>

                      <NumericInputLabel
                        name="newPercentageFranchise"
                        label="Franchise"
                        register={register("newPercentageFranchise", {
                          required:
                            !toExpire &&
                            user.isFranchise &&
                            !user.isNonFranchise &&
                            !agmtGroupDetails[0]?.flatRatePerPolicy,
                        })}
                        disabled={
                          watch("newPercentage") === "" ||
                          watch("newPercentage") === "0"
                        }
                        step="0.01"
                        min="0.00"
                        max="100"
                        width="w-10"
                        hidden={
                          agmtGroupDetails[0]?.flatRatePerPolicy ||
                          !user.isFranchise
                        }
                        required={
                          !toExpire &&
                          user.isFranchise &&
                          !user.isNonFranchise &&
                          !agmtGroupDetails[0]?.flatRatePerPolicy
                        }
                        type="number"
                        className={
                          errors.newPercentageFranchise &&
                          "border-2 border-red-500 col-span-2 "
                        }
                        pattern="\b(0*(?:[1-9][0-9]?|100))\b"
                        onBlur={() => {
                          setValue(
                            "newPercentageFranchise",
                            parseFloat(watch("newPercentageFranchise")).toFixed(
                              2
                            )
                          );
                        }}
                        secondDiv="grid grid-cols-1"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 justify-items-end gap-1 ">
                      <div className="md:col-start-1 w-full">
                        <NumericInputLabel
                          name="renewPercentageConfie"
                          label="Confie"
                          register={register("renewPercentageConfie", {
                            required:
                              !toExpire &&
                              user.isFranchise &&
                              !user.isNonFranchise &&
                              !agmtGroupDetails[0]?.flatRatePerPolicy,
                          })}
                          disabled={
                            watch("renewalPercentage") === "" ||
                            watch("renewalPercentage") === "0"
                          }
                          step="0.01"
                          min="0"
                          max="100.00"
                          width="w-10"
                          hidden={
                            agmtGroupDetails[0]?.flatRatePerPolicy &&
                            !user.isFranchise
                          }
                          required={
                            !toExpire &&
                            user.isFranchise &&
                            !user.isNonFranchise &&
                            !agmtGroupDetails[0]?.flatRatePerPolicy
                          }
                          type="number"
                          className={
                            errors.renewPercentageConfie &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          pattern="\b(0*(?:[1-9][0-9]?|100))\b"
                          onBlur={(e: any) => {
                            if (e.target.value) {
                              setValue(
                                "renewPercentageConfie",
                                parseFloat(e.target.value).toFixed(2),
                                {
                                  shouldDirty:
                                    parseFloat(e.target.value).toFixed(2) !==
                                    agmtGroupDetails[0]?.renewPercentageConfie
                                      ? true
                                      : false,
                                }
                              );
                              const renewFran = (
                                parseInt(watch("renewalPercentage")) -
                                e.target.value
                              ).toString();
                              setValue(
                                "renewPercentageFranchise",
                                parseFloat(renewFran).toFixed(2)
                              );
                            }
                          }}
                          secondDiv="grid grid-cols-1"
                        />
                      </div>
                      <NumericInputLabel
                        name="renewPercentageFranchise"
                        label="Franchise"
                        register={register("renewPercentageFranchise", {
                          required:
                            !toExpire &&
                            user.isFranchise &&
                            !user.isNonFranchise &&
                            !agmtGroupDetails[0]?.flatRatePerPolicy,
                        })}
                        disabled={
                          watch("renewalPercentage") === "" ||
                          watch("renewalPercentage") === "0"
                        }
                        step="0.01"
                        min="0"
                        max="100.00"
                        width="w-10"
                        hidden={
                          agmtGroupDetails[0]?.flatRatePerPolicy ||
                          !user.isFranchise
                        }
                        required={
                          !toExpire &&
                          user.isFranchise &&
                          !user.isNonFranchise &&
                          !agmtGroupDetails[0]?.flatRatePerPolicy
                        }
                        type="number"
                        className={
                          errors.renewPercentageFranchise &&
                          "border-2 border-red-500 col-span-2 "
                        }
                        pattern="\b(0*(?:[1-9][0-9]?|100))\b"
                        onBlur={() => {
                          setValue(
                            "renewPercentageFranchise",
                            parseFloat(
                              watch("renewPercentageFranchise")
                            ).toFixed(2)
                          );
                        }}
                        secondDiv="grid grid-cols-1"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="attachments w-1/3 pb-2">
            <label className="flex space-x-1 justify-start expandedLabel fontExpand">
              Attachments:
            </label>
            {(mainOldAttachments && mainOldAttachments.length > 0) ||
            editMainState ? (
              <AttachmentModalMainEdit
                setOldAttachments={setMainOldAttachments}
                setNewAttachments={setMainNewAttachments}
                setForDeleteAttachments={setForDeleteAttachments}
                oldAttachments={mainOldAttachments}
                newAttachments={mainNewAttachments}
                forDeleteAttachments={forDeleteAttachments}
              />
            ) : (
              <div className="flex justify-center text-xs italic col-span-3">
                No Attachment Found
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default AgrmtMasterEditForm;
